import { useTranslation } from "react-i18next";
import { CloseCircleFilled, SearchOutlined } from "@ant-design/icons";
import type { SchemaRegion } from "../../../api/restaurantSchema";
import { NewRegionButton } from "../restaurant-schema/RegionTabs";
import RegionSegmentedTabs from "../../../components/restaurant-schema/RegionSegmentedTabs";

/** Търсачката по номер: Esc изчиства (само ако има текст — иначе е „назад"). */
const TableSearchInput = ({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  className: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className={`relative ${className}`}>
      <SearchOutlined className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[14px] text-muted" />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && value) {
            event.preventDefault();
            onChange("");
          }
        }}
        placeholder={t("tablesConfig.searchPlaceholder")}
        aria-label={t("tablesConfig.searchPlaceholder")}
        className="h-[42px] w-full rounded-[11px] border border-shade-3 bg-surface pl-10 pr-9 text-[16px] text-ink outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-faint focus:border-brand focus:shadow-[0_0_0_3px_rgba(217,115,13,0.15)] sm:text-[13.5px] [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={t("tablesConfig.clearSearch")}
          className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-[14px] text-faint transition-colors duration-150 hover:text-body"
        >
          <CloseCircleFilled />
        </button>
      )}
    </div>
  );
};

/**
 * Горният ред на страницата: регионите (сегментиран контрол) + „Нов регион"
 * вляво, търсачката вдясно. На телефон горният ред е САМО за регионите (цяла
 * ширина, скрол), а търсачката и „Нов регион" са на реда под тях.
 */
const TablesConfigHeader = ({
  regions,
  activeRegionId,
  search,
  isNarrow,
  onRegionChange,
  onCreateRegion,
  onSearchChange,
}: {
  regions: SchemaRegion[];
  activeRegionId: number;
  search: string;
  isNarrow: boolean;
  onRegionChange: (regionId: number) => void;
  onCreateRegion: () => void;
  onSearchChange: (value: string) => void;
}) => {
  const tabs = <RegionSegmentedTabs regions={regions} activeId={activeRegionId} onChange={onRegionChange} />;
  if (isNarrow) {
    return (
      <div className="flex flex-col gap-2.5">
        {tabs}
        <div className="flex items-center gap-2">
          <TableSearchInput value={search} onChange={onSearchChange} className="min-w-0 flex-1" />
          <NewRegionButton onClick={onCreateRegion} />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2.5">
        {tabs}
        <NewRegionButton onClick={onCreateRegion} />
      </div>
      <TableSearchInput value={search} onChange={onSearchChange} className="w-[200px] shrink-0 lg:w-[260px]" />
    </div>
  );
};

export default TablesConfigHeader;
